/**
 * SessionHeader Component
 * 
 * Displays session status, timer, audio waveform, and end session button
 */

import { useTranslation } from 'react-i18next';
import { PauseIcon, PlayIcon, ArrowLeftIcon, PhoneIcon } from '@heroicons/react/24/solid';
import AudioWaveform from '../common/AudioWaveform';
import InputModeToggle from './InputModeToggle';

type SessionStatus = 'ready' | 'active' | 'finished';

interface SessionHeaderProps {
  sessionStatus: SessionStatus;
  formattedDuration?: string;
  isAudioPlaying?: boolean;
  getAudioFrequencyData?: () => Uint8Array;
  onEndSession?: () => void;
  onPause?: () => void;
  onResume?: () => void;
  isPaused?: boolean;
  isPauseWaiting?: boolean;
  isRecording?: boolean; // NEW: REAL mic recording state (reflects actual mic status)
  isUserSpeaking?: boolean; // PTT: user is actively holding the button/spacebar
  onCancel?: () => void; // NEW: Back/Cancel button (desktop only)
  onRequestExitConfirmation?: () => void; // Request to show exit confirmation dialog
  shouldGlow?: boolean; // NEW: Glow effect for end button when conversation ending detected
  waitingForAIGreeting?: boolean; // 🎯 Disable controls during AI greeting overlay
  canEndSession?: boolean; // 🔢 Enable/disable end session button based on exchange count

  // Input mode (PTT / Voice / Keyboard)
  inputMode?: 'voice' | 'keyboard' | 'ptt';
  onInputModeToggle?: (mode: 'voice' | 'keyboard' | 'ptt', options?: { enableMicrophone?: boolean }) => void;
  // Session input mode: if false, trainees can only use voice (no keyboard/text input)
  allowKeyboardInput?: boolean;

  // Character info (NPC the user is talking to)
  characterName?: string;
  characterRole?: string;
  // Hide the dark gradient background (e.g. when Info modal is open over white)
  hideGradient?: boolean;
}

export default function SessionHeader({
  sessionStatus,
  formattedDuration,
  isAudioPlaying = false,
  getAudioFrequencyData,
  onEndSession,
  onPause,
  onResume,
  isPaused = false,
  isPauseWaiting = false,
  isRecording = false,
  isUserSpeaking = false,
  onCancel,
  onRequestExitConfirmation,
  shouldGlow = false,
  waitingForAIGreeting = false,
  canEndSession = true, // Default to enabled for backwards compatibility
  inputMode = 'ptt',
  onInputModeToggle,
  allowKeyboardInput = true,
  characterName,
  characterRole,
  hideGradient = false,
}: SessionHeaderProps) {
  const { t } = useTranslation();
  
  return (
    <div className={`fixed top-0 left-0 right-0 px-3 md:px-6 py-2 md:py-3 z-10 transition-all duration-300 ${hideGradient ? '' : 'bg-gradient-to-b from-black/80 to-transparent'}`}>
      {/* Row 1: Main controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 md:gap-4">
          {/* Back Button - Desktop only (mobile has native back) */}
          {(onRequestExitConfirmation || onCancel) && sessionStatus === 'active' && (
            <button
              onClick={() => {
                if (onRequestExitConfirmation) {
                  onRequestExitConfirmation();
                } else {
                  onCancel?.();
                }
              }}
              className="hidden md:flex items-center gap-1.5 px-3 py-1.5 bg-gray-700/90 hover:bg-gray-600 text-white rounded-lg transition-colors font-medium text-sm"
              title="Back to scenario info"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              <span>Back</span>
            </button>
          )}
          {/* Timer - Responsive sizing */}
          {sessionStatus === 'active' && formattedDuration && (
            <div className="text-center">
              <div className="text-lg md:text-2xl font-mono font-bold text-white leading-tight">
                {formattedDuration}
              </div>
              <div className="text-[10px] md:text-xs text-gray-300 hidden md:block">{t('scenario.session.sessionTime')}</div>
            </div>
          )}
          
          {/* Status Badge - Responsive */}
          {sessionStatus === 'ready' && (
            <div className="px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-medium bg-green-500/80 text-white">
              ⏸️ {t('scenario.session.ready')}
            </div>
          )}
          {sessionStatus === 'active' && (
            <>
              {/* REC button: in PTT mode, only show active when user is pressing the button */}
              {(inputMode === 'ptt' ? isUserSpeaking : isRecording) ? (
                // Mic is ACTUALLY recording - strong red animation
                <div className="flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 rounded-full bg-red-900/80 text-white border-2 border-red-500/50 relative">
                  <div className="relative w-2.5 md:w-3 h-2.5 md:h-3 flex items-center justify-center">
                    <div className="w-2.5 md:w-3 h-2.5 md:h-3 bg-red-500 rounded-full animate-ping absolute"></div>
                    <div className="w-2.5 md:w-3 h-2.5 md:h-3 bg-red-600 rounded-full relative shadow-xl shadow-red-600/80"></div>
                  </div>
                  <span className="text-xs md:text-sm font-extrabold">REC</span>
                </div>
              ) : (
                // Mic is paused (greeting, hints, manual pause, etc)
                <div className="flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 rounded-full bg-gray-600/80 text-white">
                  <div className="w-2.5 md:w-3 h-2.5 md:h-3 bg-gray-400 rounded-full"></div>
                  <span className="text-xs md:text-sm font-bold opacity-60">REC</span>
                </div>
              )}
            </>
          )}

          {/* 🎵 Audio Waveform - After REC badge */}
          {sessionStatus === 'active' && getAudioFrequencyData && (
            <div className="scale-90 md:scale-100">
              <AudioWaveform
                isActive={isAudioPlaying}
                barCount={7}
                colorFrom="rgb(52, 211, 153)" // emerald-400
                colorTo="rgb(16, 185, 129)"   // emerald-500
                getFrequencyData={getAudioFrequencyData}
              />
            </div>
          )}
          {sessionStatus === 'finished' && (
            <div className="px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-medium bg-gray-500/80 text-white">
              ✅ {t('scenario.session.completed')}
            </div>
          )}
        </div>
      
        {/* Action Buttons - Responsive */}
        {/* Desktop: Hidden (using centered SessionControlButtons instead) */}
        {/* Mobile: Horizontal layout (3 buttons) - mute avatar omitted (use device volume) */}
        {sessionStatus === 'active' && (
          <div className="flex md:hidden items-center gap-1.5 mr-[15px]">
            {/* Input Mode Toggle - only show if keyboard input is allowed */}
            {allowKeyboardInput && onInputModeToggle && (
              <InputModeToggle
                currentMode={inputMode}
                onToggle={(mode) => onInputModeToggle(mode, { enableMicrophone: true })}
                variant="mobile"
                disabled={waitingForAIGreeting}
              />
            )}
            
            {/* Pause/Resume Button */}
            {onPause && onResume && (
              isPaused ? (
                <button
                  onClick={onResume}
                  className="w-10 h-10 rounded-full bg-blue-500 hover:bg-blue-600 active:bg-blue-700 shadow-md hover:shadow-lg transition-all duration-200 transform active:scale-95 flex items-center justify-center"
                  title={t('scenario.session.resume')}
                  aria-label={t('scenario.session.resume')}
                >
                  <PlayIcon className="w-5 h-5 text-white ml-0.5" />
                </button>
              ) : (
                <button
                  onClick={onPause}
                  disabled={isPauseWaiting}
                  className={`w-10 h-10 rounded-full shadow-md hover:shadow-lg transition-all duration-200 transform active:scale-95 flex items-center justify-center ${
                    isPauseWaiting 
                      ? 'bg-gray-400 cursor-wait' 
                      : 'bg-yellow-500 hover:bg-yellow-600 active:bg-yellow-700'
                  }`}
                  title={isPauseWaiting ? t('scenario.session.waitingForAvatarToStop') : t('scenario.session.pause')}
                  aria-label={isPauseWaiting ? t('scenario.session.waitingForAvatarToStop') : t('scenario.session.pause')}
                >
                  {isPauseWaiting ? (
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <PauseIcon className="w-5 h-5 text-white" />
                  )}
                </button>
              )
            )}
            
            {/* Stop & Evaluate Button - Extra large for easy tapping, pushed down to extend below header */}
            {onEndSession && (
              <button
                onClick={onEndSession}
                disabled={!canEndSession}
                className={`w-16 h-16 rounded-full shadow-md hover:shadow-lg transition-all duration-200 transform active:scale-95 flex items-center justify-center relative mt-2 ${
                  canEndSession
                    ? 'bg-red-500 hover:bg-red-600 active:bg-red-700 cursor-pointer'
                    : 'bg-gray-400 cursor-not-allowed opacity-50'
                } ${
                  shouldGlow && canEndSession ? 'animate-pulse-glow' : ''
                }`}
                title={canEndSession ? t('scenario.session.stopAndEvaluate') : t('scenario.session.waitingForExchanges', 'Wait for at least 3 exchanges')}
                aria-label={canEndSession ? t('scenario.session.stopAndEvaluate') : t('scenario.session.waitingForExchanges', 'Wait for at least 3 exchanges')}
              >
                {shouldGlow && canEndSession && (
                  <>
                    <div className="absolute inset-0 rounded-full bg-red-400 animate-ping opacity-75 pointer-events-none"></div>
                    <div className="absolute inset-0 rounded-full bg-red-400 blur-xl opacity-50 animate-pulse pointer-events-none"></div>
                  </>
                )}
                <PhoneIcon className="w-8 h-8 text-white transform rotate-[135deg] relative z-10" />
              </button>
            )}
          </div>
        )}
      </div>{/* End Row 1 */}
      
      {/* Row 2: Character Name & Role - second line below controls */}
      {sessionStatus === 'active' && characterName && (
        <div className="mt-1 flex flex-wrap items-center gap-1.5 max-w-[60%]">
          <span className="text-xs md:text-sm font-semibold text-white/90">
            {characterName}
          </span>
          {characterRole && (
            <>
              <span className="text-white/30">—</span>
              <span className="text-[10px] md:text-xs text-gray-300/80 italic">
                {characterRole}
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

